/**
 * The chat panel.
 *
 * Rendered entirely through DOM APIs rather than innerHTML. Assistant text
 * originates from a model and passes through a knowledge base, so treating it
 * as markup at any point would be an XSS sink; `textContent` makes that
 * structurally impossible rather than a matter of remembering to escape.
 *
 * Accessibility is built in, not retrofitted: the panel is a labelled dialog,
 * focus is trapped while it is open and restored when it closes, replies are
 * announced through a live region, and every control is reachable and visibly
 * focusable by keyboard.
 */

import { styles } from '../styles/widget';
import { strings, direction } from './i18n';
import { parseMessageBlocks, splitLinkRuns } from './messageFormat';
import {
    ChatResponse,
    NavigationAction,
    ResolvedLanguage,
    SourceReference,
    ThemeTokens
} from './types';
import {
    isCompactLayout,
    isKeyboardOpen,
    visualViewportBox
} from './viewport';

const ICONS = {
    chat: 'M12 3c5 0 9 3.36 9 7.5 0 4.14-4 7.5-9 7.5-.98 0-1.92-.13-2.8-.37L4 20l1.2-3.3C3.83 15.4 3 13.05 3 10.5 3 6.36 7 3 12 3z',
    close: 'M6 6l12 12M18 6L6 18',
    minimize: 'M6 12h12',
    // Points left-to-right. Arabic flips it with CSS scaleX(-1).
    send: 'M5 12h14M13 6l6 6-6 6',
    open: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5'
};

function icon(path: string): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');

    const element = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    element.setAttribute('d', path);
    svg.appendChild(element);
    return svg;
}

export interface UiCallbacks {
    onSend: (text: string) => void;
    onSuggestion: (text: string) => void;
    onNavigate: (action: NavigationAction) => void;
    onClear: () => void;
    onOpen: () => void;
    onClose: () => void;
    onRetry: () => void;
}

export type ConnectionStatus = 'online' | 'offline' | 'connecting';

export interface UiConfig {
    title: string;
    subtitle?: string;
    logoUrl?: string;
    language: ResolvedLanguage;
    theme: ThemeTokens;
    position: 'bottom-right' | 'bottom-left';
    zIndex: number;
    maxMessageLength: number;
}

export class ChatUi {
    private readonly host: HTMLElement;
    private readonly shadow: ShadowRoot;
    private readonly root: HTMLDivElement;
    private readonly fab: HTMLButtonElement;
    private readonly panel: HTMLDivElement;
    private readonly messages: HTMLDivElement;
    private readonly suggestions: HTMLDivElement;
    private readonly input: HTMLTextAreaElement;
    private readonly sendButton: HTMLButtonElement;
    private readonly errorBanner: HTMLDivElement;
    private readonly errorText: HTMLSpanElement;
    private readonly liveRegion: HTMLDivElement;
    private readonly titleElement: HTMLDivElement;
    private readonly subtitleElement: HTMLDivElement;
    private readonly logoElement: HTMLImageElement;
    private readonly statusElement: HTMLDivElement;
    private readonly statusDot: HTMLSpanElement;
    private readonly statusText: HTMLSpanElement;

    private typingRow: HTMLDivElement | null = null;
    private isOpen = false;
    private busy = false;
    private lastFocused: Element | null = null;
    private config: UiConfig;
    private connectionStatus: ConnectionStatus = 'connecting';
    private viewportCleanup: (() => void) | null = null;
    private hostScrollLock: {
        overflow: string;
        htmlOverflow: string;
        position: string;
        top: string;
        width: string;
        scrollY: number;
    } | null = null;

    constructor(
        container: HTMLElement,
        config: UiConfig,
        private readonly callbacks: UiCallbacks
    ) {
        this.config = config;

        this.host = document.createElement('div');
        // A distinctive tag-like attribute rather than a class, so a host page
        // stylesheet cannot accidentally target the widget's mount point.
        this.host.setAttribute('data-universal-chatbot', '');
        this.shadow = this.host.attachShadow({ mode: 'open' });

        const sheet = document.createElement('style');
        sheet.textContent = styles();
        this.shadow.appendChild(sheet);

        this.root = document.createElement('div');
        this.root.className = 'root';
        this.shadow.appendChild(this.root);

        this.fab = this.buildFab();
        this.panel = document.createElement('div');
        this.panel.className = 'panel';
        this.panel.hidden = true;
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'false');
        this.panel.setAttribute('aria-label', config.title);

        this.titleElement = document.createElement('div');
        this.subtitleElement = document.createElement('div');
        this.logoElement = document.createElement('img');
        this.statusElement = document.createElement('div');
        this.statusDot = document.createElement('span');
        this.statusText = document.createElement('span');
        this.messages = document.createElement('div');
        this.suggestions = document.createElement('div');
        this.input = document.createElement('textarea');
        this.sendButton = document.createElement('button');
        this.errorBanner = document.createElement('div');
        this.errorText = document.createElement('span');
        this.liveRegion = document.createElement('div');

        this.buildPanel();

        this.root.appendChild(this.panel);
        this.root.appendChild(this.fab);

        this.applyConfig(config);
        container.appendChild(this.host);
    }

    // ---------------------------------------------------------------- build

    private buildFab(): HTMLButtonElement {
        const button = document.createElement('button');
        button.className = 'fab';
        button.type = 'button';
        button.setAttribute('aria-haspopup', 'dialog');
        button.appendChild(icon(ICONS.chat));
        button.addEventListener('click', () => this.open());
        return button;
    }

    private buildPanel(): void {
        // --- header ---
        const header = document.createElement('div');
        header.className = 'header';

        this.logoElement.className = 'header-logo';
        this.logoElement.alt = '';
        this.logoElement.hidden = true;
        this.logoElement.addEventListener('error', () => {
            this.logoElement.hidden = true;
        });

        const text = document.createElement('div');
        text.className = 'header-text';
        this.titleElement.className = 'header-title';
        this.subtitleElement.className = 'header-subtitle';
        this.subtitleElement.hidden = true;

        this.statusElement.className = 'header-status';
        this.statusElement.dataset['state'] = this.connectionStatus;
        this.statusDot.className = 'status-dot';
        this.statusElement.append(this.statusDot, this.statusText);

        text.append(this.titleElement, this.subtitleElement, this.statusElement);

        const minimizeButton = document.createElement('button');
        minimizeButton.className = 'icon-button';
        minimizeButton.type = 'button';
        minimizeButton.appendChild(icon(ICONS.minimize));
        minimizeButton.addEventListener('click', () => this.minimize());

        const closeButton = document.createElement('button');
        closeButton.className = 'icon-button';
        closeButton.type = 'button';
        closeButton.appendChild(icon(ICONS.close));
        closeButton.addEventListener('click', () => this.close());

        header.append(this.logoElement, text, minimizeButton, closeButton);

        // --- conversation ---
        this.messages.className = 'messages';
        this.messages.setAttribute('role', 'log');
        this.messages.setAttribute('aria-live', 'polite');
        this.messages.setAttribute('aria-relevant', 'additions');
        this.messages.tabIndex = 0;

        this.suggestions.className = 'suggestions';
        this.suggestions.hidden = true;

        // --- error banner ---
        this.errorBanner.className = 'error-banner';
        this.errorBanner.hidden = true;
        this.errorBanner.setAttribute('role', 'alert');
        const retry = document.createElement('button');
        retry.className = 'error-retry';
        retry.type = 'button';
        retry.addEventListener('click', () => {
            this.hideError();
            this.callbacks.onRetry();
        });
        this.errorBanner.append(this.errorText, retry);

        // --- composer ---
        const composer = document.createElement('div');
        composer.className = 'composer';

        this.input.className = 'input';
        this.input.rows = 1;
        this.input.maxLength = this.config.maxMessageLength;
        this.input.addEventListener('keydown', (event) => this.onKeyDown(event));
        this.input.addEventListener('input', () => this.autoGrow());
        this.input.addEventListener('focus', () => this.onComposerFocus());
        this.input.addEventListener('blur', () => this.onComposerBlur());

        this.sendButton.className = 'send';
        this.sendButton.type = 'button';
        this.sendButton.appendChild(icon(ICONS.send));
        this.sendButton.addEventListener('click', () => this.submit());

        composer.append(this.input, this.sendButton);

        this.liveRegion.className = 'sr-only';
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('role', 'status');

        this.panel.append(
            header,
            this.messages,
            this.errorBanner,
            composer,
            this.liveRegion
        );

        this.storeControls(minimizeButton, closeButton, retry);
        this.panel.addEventListener('keydown', (event) => this.onPanelKeyDown(event));
    }

    private controls: {
        minimize: HTMLButtonElement;
        close: HTMLButtonElement;
        retry: HTMLButtonElement;
    } | null = null;

    private storeControls(
        minimize: HTMLButtonElement,
        close: HTMLButtonElement,
        retry: HTMLButtonElement
    ): void {
        this.controls = { minimize, close, retry };
    }

    // --------------------------------------------------------------- config

    public applyConfig(config: UiConfig): void {
        this.config = config;
        const copy = strings(config.language);
        const dir = direction(config.language);

        this.host.setAttribute('dir', dir);
        this.root.setAttribute('dir', dir);
        this.root.setAttribute('lang', config.language);
        this.root.dataset['position'] = config.position;

        const theme = config.theme;
        const variables: Record<string, string> = {
            '--uc-primary': theme.primaryColor,
            '--uc-on-primary': theme.onPrimaryColor,
            '--uc-surface': theme.surfaceColor,
            '--uc-panel': theme.panelColor,
            '--uc-text': theme.textColor,
            '--uc-muted': theme.mutedTextColor,
            '--uc-border': theme.borderColor,
            '--uc-radius': theme.borderRadius,
            '--uc-font': theme.fontFamily,
            '--uc-user-bubble': theme.userBubbleColor,
            '--uc-user-bubble-text': theme.userBubbleTextColor,
            '--uc-bot-bubble': theme.botBubbleColor,
            '--uc-z': String(config.zIndex)
        };
        for (const [name, value] of Object.entries(variables)) {
            this.root.style.setProperty(name, value);
        }

        this.titleElement.textContent = config.title;
        this.panel.setAttribute('aria-label', config.title);
        this.applyLogo(config.logoUrl, config.title);
        this.applySubtitle(config.subtitle);
        this.refreshStatusCopy();
        this.fab.setAttribute('aria-label', copy.open);
        this.fab.title = copy.open;
        this.input.placeholder = copy.placeholder;
        this.input.setAttribute('aria-label', copy.placeholder);
        this.input.maxLength = config.maxMessageLength;
        this.sendButton.setAttribute('aria-label', copy.send);
        this.sendButton.title = copy.send;
        this.messages.setAttribute('aria-label', copy.conversationLabel);
        this.suggestions.setAttribute('aria-label', copy.suggestionsLabel);

        if (this.controls !== null) {
            this.controls.minimize.setAttribute('aria-label', copy.minimize);
            this.controls.minimize.title = copy.minimize;
            this.controls.close.setAttribute('aria-label', copy.close);
            this.controls.close.title = copy.close;
            this.controls.retry.textContent = copy.retry;
        }
    }

    private applyLogo(logoUrl: string | undefined, title: string): void {
        if (logoUrl === undefined || !isSafeAssetUrl(logoUrl)) {
            this.logoElement.removeAttribute('src');
            this.logoElement.hidden = true;
            return;
        }
        this.logoElement.alt = title;
        this.logoElement.src = logoUrl;
        this.logoElement.hidden = false;
    }

    private applySubtitle(subtitle: string | undefined): void {
        const text = (subtitle ?? '').trim();
        this.subtitleElement.textContent = text;
        this.subtitleElement.hidden = text === '';
    }

    public setConnectionStatus(status: ConnectionStatus): void {
        this.connectionStatus = status;
        this.statusElement.dataset['state'] = status;
        this.refreshStatusCopy();
    }

    private refreshStatusCopy(): void {
        const copy = strings(this.config.language);
        const label =
            this.connectionStatus === 'online'
                ? copy.statusOnline
                : this.connectionStatus === 'offline'
                  ? copy.statusOffline
                  : copy.statusConnecting;
        this.statusText.textContent = label;
        this.statusElement.setAttribute('aria-label', label);
    }

    // ------------------------------------------------------------ behaviour

    private onKeyDown(event: KeyboardEvent): void {
        // Enter sends, Shift+Enter inserts a newline - the convention every
        // chat interface uses, and the one users try first.
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.submit();
        }
    }

    private onPanelKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }

        // Focus trap. Without it, tabbing out of an open panel lands the user
        // in the host page with no visible indication of where they are.
        const focusable = this.focusableElements();
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = this.shadow.activeElement;

        if (event.shiftKey && active === first && last !== undefined) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last && first !== undefined) {
            event.preventDefault();
            first.focus();
        }
    }

    private focusableElements(): HTMLElement[] {
        return Array.from(
            this.panel.querySelectorAll<HTMLElement>(
                'button:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])'
            )
        ).filter((element) => element.offsetParent !== null || element === this.input);
    }

    private autoGrow(): void {
        this.input.style.height = 'auto';
        this.input.style.height = `${Math.min(120, this.input.scrollHeight)}px`;
    }

    private submit(): void {
        const text = this.input.value.trim();
        if (text === '' || this.busy) {
            return;
        }
        this.input.value = '';
        this.autoGrow();
        this.callbacks.onSend(text);
    }

    public open(): void {
        if (this.isOpen) {
            return;
        }
        this.lastFocused = document.activeElement;
        this.isOpen = true;
        this.panel.hidden = false;
        this.fab.hidden = true;
        this.bindViewport();
        this.callbacks.onOpen();
        // On iPhone, focusing the field opens the keyboard immediately and
        // covers the welcome chips. Let the user tap the composer instead.
        if (!this.isCompact()) {
            window.setTimeout(() => this.input.focus(), 0);
        }
    }

    /** Hides the panel and keeps the conversation as it is. */
    public minimize(): void {
        this.hidePanel();
    }

    /** Hides the panel and ends the conversation. */
    public close(): void {
        this.hidePanel();
        this.callbacks.onClose();
    }

    private hidePanel(): void {
        if (!this.isOpen) {
            return;
        }
        this.isOpen = false;
        this.unbindViewport();
        this.unlockHostScroll();
        this.panel.hidden = true;
        this.fab.hidden = false;

        if (this.lastFocused instanceof HTMLElement) {
            this.lastFocused.focus();
        } else {
            this.fab.focus();
        }
    }

    public toggle(): void {
        if (this.isOpen) {
            this.minimize();
        } else {
            this.open();
        }
    }

    public get opened(): boolean {
        return this.isOpen;
    }

    // -------------------------------------------------------------- content

    public addMessage(role: 'user' | 'assistant', content: string): HTMLDivElement {
        this.clearEmptyState();

        const row = document.createElement('div');
        row.className = 'msg';
        row.dataset['role'] = role;

        const bubble = document.createElement('div');
        bubble.className = 'bubble';
        // Each bubble infers its own direction from its first strong
        // character. Without this, an English answer inside an Arabic-mode
        // panel inherits RTL and renders its punctuation on the wrong side -
        // and a bilingual conversation always contains both.
        bubble.setAttribute('dir', 'auto');
        this.fillBubble(bubble, role, content);

        const time = document.createElement('div');
        time.className = 'timestamp';
        time.textContent = new Intl.DateTimeFormat(
            this.config.language === 'ar' ? 'ar' : 'en',
            { hour: '2-digit', minute: '2-digit' }
        ).format(new Date());

        row.append(bubble, time);
        this.messages.appendChild(row);
        this.scrollToBottom();

        if (role === 'assistant') {
            this.liveRegion.textContent = content;
        }
        return row;
    }

    /**
     * Renders assistant text as paragraphs and lists.
     *
     * Every string still goes through textContent. The parser only decides
     * which element a line belongs in; it never interprets markup.
     */
    private fillBubble(
        bubble: HTMLDivElement,
        role: 'user' | 'assistant',
        content: string
    ): void {
        if (role === 'user') {
            bubble.textContent = content;
            return;
        }

        const blocks = parseMessageBlocks(content);
        if (blocks.length === 0) {
            return;
        }

        for (const block of blocks) {
            if (block.type === 'paragraph') {
                const paragraph = document.createElement('p');
                paragraph.className = 'bubble-p';
                this.appendLinkedText(paragraph, block.text);
                bubble.appendChild(paragraph);
                continue;
            }

            const list = document.createElement('ul');
            list.className = 'bubble-list';
            for (const item of block.items) {
                const entry = document.createElement('li');
                this.appendLinkedText(entry, item);
                list.appendChild(entry);
            }
            bubble.appendChild(list);
        }
    }

    private appendLinkedText(host: HTMLElement, text: string): void {
        for (const run of splitLinkRuns(text)) {
            if (run.type === 'text') {
                host.appendChild(document.createTextNode(run.value));
                continue;
            }
            if (!isSafeAssetUrl(run.value)) {
                host.appendChild(document.createTextNode(run.value));
                continue;
            }
            const link = document.createElement('a');
            link.className = 'bubble-link';
            link.href = run.value;
            link.textContent = run.value;
            const action: NavigationAction = {
                type: 'WEB',
                key: 'knowledge-link',
                label: run.value,
                url: run.value,
                webUrl: run.value
            };
            link.addEventListener('click', (event) => {
                event.preventDefault();
                this.callbacks.onNavigate(action);
            });
            host.appendChild(link);
        }
    }

    public addNavigation(row: HTMLDivElement, action: NavigationAction): void {
        const href = action.webUrl ?? action.url;
        if (!isSafeAssetUrl(href)) {
            return;
        }

        const card = document.createElement('div');
        card.className = 'nav-card';

        const link = document.createElement('a');
        link.className = 'nav-link';
        link.href = href;
        link.textContent = action.label;
        link.addEventListener('click', (event) => {
            event.preventDefault();
            this.callbacks.onNavigate(action);
        });

        card.appendChild(link);
        row.appendChild(card);
        this.scrollToBottom();
    }

    public addSources(row: HTMLDivElement, sources: SourceReference[]): void {
        if (sources.length === 0) {
            return;
        }
        const copy = strings(this.config.language);

        const details = document.createElement('details');
        details.className = 'sources';

        const summary = document.createElement('summary');
        summary.className = 'sources-summary';
        summary.textContent = `${copy.sourcesLabel} (${sources.length})`;
        details.appendChild(summary);

        for (const source of sources) {
            const item = document.createElement('div');
            item.className = 'source-item';

            const label =
                source.fileName !== undefined && source.fileName.trim() !== ''
                    ? source.fileName
                    : source.title;

            if (source.url !== undefined && isSafeAssetUrl(source.url)) {
                const button = document.createElement('button');
                button.className = 'source-link';
                button.type = 'button';
                button.appendChild(icon(ICONS.open));
                const text = document.createElement('span');
                text.textContent = label;
                button.appendChild(text);
                button.setAttribute('aria-label', `${copy.openPage}: ${label}`);
                const action: NavigationAction = {
                    type: 'WEB',
                    key: source.id,
                    label: source.title,
                    url: source.url
                };
                button.addEventListener('click', () =>
                    this.callbacks.onNavigate(action)
                );
                item.appendChild(button);
            } else {
                item.textContent = label;
            }
            details.appendChild(item);
        }

        row.appendChild(details);
    }

    public setSuggestions(items: string[]): void {
        this.suggestions.replaceChildren();

        if (items.length === 0) {
            this.suggestions.hidden = true;
            this.detachSuggestions();
            return;
        }

        for (const item of items) {
            const chip = document.createElement('button');
            chip.className = 'chip';
            chip.type = 'button';
            chip.textContent = item;
            chip.addEventListener('click', () => this.callbacks.onSuggestion(item));
            this.suggestions.appendChild(chip);
        }
        this.suggestions.hidden = false;
        this.placeSuggestionsAfterLastAssistant();
    }

    /** Chips sit under the latest assistant bubble, after the welcome line. */
    private placeSuggestionsAfterLastAssistant(): void {
        const assistants = this.messages.querySelectorAll<HTMLDivElement>(
            '.msg[data-role="assistant"]'
        );
        const last = assistants[assistants.length - 1];
        if (last === undefined) {
            this.messages.appendChild(this.suggestions);
            return;
        }
        if (last.nextSibling !== null) {
            this.messages.insertBefore(this.suggestions, last.nextSibling);
        } else {
            this.messages.appendChild(this.suggestions);
        }
    }

    private detachSuggestions(): void {
        if (this.suggestions.parentElement === this.messages) {
            this.suggestions.remove();
        }
    }

    public showEmptyState(): void {
        const copy = strings(this.config.language);
        const empty = document.createElement('div');
        empty.className = 'empty-state';
        empty.textContent = copy.emptyState;
        this.messages.appendChild(empty);
    }

    private clearEmptyState(): void {
        this.messages.querySelector('.empty-state')?.remove();
    }

    public clearMessages(): void {
        this.detachSuggestions();
        this.messages.replaceChildren();
        this.typingRow = null;
    }

    public replayTurns(
        turns: Array<{ role: 'user' | 'assistant'; content: string }>
    ): void {
        this.clearMessages();
        for (const turn of turns) {
            this.addMessage(turn.role, turn.content);
        }
    }

    public setBusy(busy: boolean): void {
        this.busy = busy;
        this.sendButton.disabled = busy;
        this.input.setAttribute('aria-busy', String(busy));

        if (busy) {
            this.showTyping();
        } else {
            this.hideTyping();
        }
    }

    private showTyping(): void {
        if (this.typingRow !== null) {
            return;
        }
        this.clearEmptyState();

        const row = document.createElement('div');
        row.className = 'msg';
        row.dataset['role'] = 'assistant';

        const bubble = document.createElement('div');
        bubble.className = 'bubble typing';
        bubble.setAttribute('aria-label', strings(this.config.language).thinking);
        for (let i = 0; i < 3; i += 1) {
            bubble.appendChild(document.createElement('span'));
        }

        row.appendChild(bubble);
        this.messages.appendChild(row);
        this.typingRow = row;
        this.scrollToBottom();
    }

    private hideTyping(): void {
        this.typingRow?.remove();
        this.typingRow = null;
    }

    public showError(text: string): void {
        this.errorText.textContent = text;
        this.errorBanner.hidden = false;
    }

    public hideError(): void {
        this.errorBanner.hidden = true;
    }

    public applyResponse(response: ChatResponse): void {
        const row = this.addMessage('assistant', response.message.content);

        if (response.navigation !== null) {
            this.addNavigation(row, response.navigation);
        }
        this.setSuggestions(response.suggestions);
    }

    private scrollToBottom(): void {
        window.requestAnimationFrame(() => {
            this.messages.scrollTop = this.messages.scrollHeight;
        });
    }

    private isCompact(): boolean {
        return isCompactLayout(window.innerWidth);
    }

    private onComposerFocus(): void {
        this.syncViewport();
        window.setTimeout(() => this.syncViewport(), 80);
        window.setTimeout(() => this.syncViewport(), 320);
    }

    private onComposerBlur(): void {
        window.setTimeout(() => this.syncViewport(), 80);
        window.setTimeout(() => this.syncViewport(), 320);
    }

    private bindViewport(): void {
        this.unbindViewport();
        const onChange = (): void => this.syncViewport();
        const visual = window.visualViewport;
        visual?.addEventListener('resize', onChange);
        visual?.addEventListener('scroll', onChange);
        window.addEventListener('resize', onChange);
        window.addEventListener('orientationchange', onChange);
        const orientationTimer = window.setTimeout(onChange, 280);
        this.viewportCleanup = () => {
            window.clearTimeout(orientationTimer);
            visual?.removeEventListener('resize', onChange);
            visual?.removeEventListener('scroll', onChange);
            window.removeEventListener('resize', onChange);
            window.removeEventListener('orientationchange', onChange);
        };
        this.syncViewport();
    }

    private unbindViewport(): void {
        this.viewportCleanup?.();
        this.viewportCleanup = null;
        this.clearViewportStyles();
    }

    private syncViewport(): void {
        if (!this.isOpen) {
            this.clearViewportStyles();
            this.unlockHostScroll();
            return;
        }

        if (!this.isCompact()) {
            this.unlockHostScroll();
            this.clearViewportStyles();
            return;
        }

        this.lockHostScroll();
        const box = visualViewportBox(window.visualViewport, window);
        this.root.classList.add('is-compact');
        this.root.classList.toggle(
            'is-keyboard',
            isKeyboardOpen(window.innerHeight, box.height)
        );
        this.root.style.top = `${box.top}px`;
        this.root.style.left = `${box.left}px`;
        this.root.style.width = `${box.width}px`;
        this.root.style.height = `${box.height}px`;
        this.root.style.right = 'auto';
        this.root.style.bottom = 'auto';
    }

    private clearViewportStyles(): void {
        this.root.classList.remove('is-compact', 'is-keyboard');
        this.root.style.top = '';
        this.root.style.left = '';
        this.root.style.width = '';
        this.root.style.height = '';
        this.root.style.right = '';
        this.root.style.bottom = '';
    }

    private lockHostScroll(): void {
        if (this.hostScrollLock !== null) {
            return;
        }
        const body = document.body;
        const html = document.documentElement;
        this.hostScrollLock = {
            overflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
            scrollY: window.scrollY
        };
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${this.hostScrollLock.scrollY}px`;
        body.style.width = '100%';
    }

    private unlockHostScroll(): void {
        if (this.hostScrollLock === null) {
            return;
        }
        const saved = this.hostScrollLock;
        this.hostScrollLock = null;
        const body = document.body;
        const html = document.documentElement;
        html.style.overflow = saved.htmlOverflow;
        body.style.overflow = saved.overflow;
        body.style.position = saved.position;
        body.style.top = saved.top;
        body.style.width = saved.width;
        window.scrollTo(0, saved.scrollY);
    }

    public destroy(): void {
        this.unbindViewport();
        this.unlockHostScroll();
        this.host.remove();
    }
}

/** http(s) or a same-origin path. Rejects javascript: and other schemes. */
function isSafeAssetUrl(url: string): boolean {
    const trimmed = url.trim();
    if (trimmed === '' || /[\s<>"'`]/.test(trimmed)) {
        return false;
    }
    if (/^(https?:|\/)/i.test(trimmed) && !/^javascript:/i.test(trimmed)) {
        return true;
    }
    return false;
}
